import { Capacitor, registerPlugin } from "@capacitor/core";
import { LocalNotifications, type LocalNotificationSchema } from "@capacitor/local-notifications";
import { StatisticsStorage } from "../storage/statistics_storage.js";
import { LocalizationManager } from "../localization/localization_manager.js";
import { analytics } from "../analytics/index.js";
import {
  collectHttpImageUrls,
  DEFAULT_BANNERS,
  NotificationConfigService,
  RANDOM_BANNER_MARKER,
  type NotificationConfig,
  type NotificationImageConfig,
  type NotificationItemConfig,
  type SmartNotificationsConfig,
  type SmartNotificationWeekConfig
} from "./notification_config.js";

interface BigPictureScheduleOptions
{
  title: string;
  body: string;
  id: number;
  delaySeconds: number;
  style: string;
  imageUrl?: string;
  largeIconUrl?: string;
  bigPictureUrl?: string;
  extra?: string;
}

interface PendingNativeEvent
{
  id: number;
  extra: string;
  timestamp: number;
}

interface BigPictureNotificationPlugin
{
  schedule( options: BigPictureScheduleOptions ): Promise<{ success: boolean }>;
  preloadImages( options: { urls: string[] } ): Promise<{ success: boolean; count: number }>;
  cancel( options: { id: number } ): Promise<{ success: boolean }>;
  cancelMany( options: { ids: number[] } ): Promise<{ success: boolean; count: number }>;
  getPending(): Promise<{ ids?: number[] }>;
  consumePendingEvents(): Promise<{ delivered?: PendingNativeEvent[]; clicked?: PendingNativeEvent[] }>;
}

const BigPictureNotification = registerPlugin<BigPictureNotificationPlugin>( "BigPictureNotification" );

interface IScheduleNotificationOptions
{
  title: string;
  body: string;
  image?: string;
  large_icon?: string;
  big_picture?: NotificationImageConfig;
  show_large_icon?: boolean;
  show_big_picture?: boolean;
  hide_large_icon_when_expanded?: boolean;
  delay_minutes?: number;
  id?: number;
  extra?: Record<string, unknown>;
}

interface INotificationSlot
{
  slot_index: 0 | 1 | 2;
  prefix: string;
  hour: number;
  minute: number;
}

interface INotificationExtra
{
  key?: string;
  scheduled_at?: number;
  text_pair?: number | string;
}

interface IStoredNotificationRecord
{
  id: number;
  key: string | undefined;
  at: number;
}

export interface IPendingNotificationsResult
{
  local_notifications: LocalNotificationSchema[];
  big_picture_ids: number[];
}

enum NotificationStyle
{
  LargeIcon     = 'largeIcon',
  BigPicture    = 'bigPicture',
  Both          = 'both',
  BothHideLarge = 'bothHideLarge',
  None          = 'none'
}

const SLOT_DEFS: { slot_index: 0 | 1 | 2; key: keyof SmartNotificationWeekConfig; prefix: string }[] = [
  { slot_index: 0, key: 'morning',   prefix: 'MORNING'   },
  { slot_index: 1, key: 'afternoon', prefix: 'AFTERNOON' },
  { slot_index: 2, key: 'evening',   prefix: 'EVENING'   }
];

const pickRandomImage = ( images: string[] ): string | undefined =>
  images.length > 0 ? images[ Math.floor( Math.random() * images.length ) ] : undefined;

const resolveNotificationImageUrl = ( image?: NotificationImageConfig ): string | undefined =>
{
  if ( Array.isArray( image ) ) return pickRandomImage( image );
  if ( image !== RANDOM_BANNER_MARKER ) return image;
  return pickRandomImage( DEFAULT_BANNERS );
};

const parseExtra = ( raw: unknown ): INotificationExtra =>
{
  if ( typeof raw === 'string' )
  {
    try { return JSON.parse( raw || '{}' ) as INotificationExtra; }
    catch { return {}; }
  }
  return ( raw && typeof raw === 'object' ) ? raw as INotificationExtra : {};
};

export class NotificationManager
{
  private static readonly SESSION_PUSH_ID          = 200;
  private static readonly DAILY_SLOTS_START_ID     = 300;
  private static readonly SLOTS_PER_DAY            = 3;
  private static readonly MAX_CANCEL_DAYS          = 60;
  private static readonly MAX_STORED_NOTIFICATIONS = 100;
  private static readonly SESSION_INTERVAL_MS      = 60_000;
  private static readonly RANDOM_TEXTS_PER_SLOT    = 10;
  private static readonly DEBUG_TEST_DELAY_MINUTES = 1 / 60;
  private static readonly DELIVERED_STORAGE_KEY    = 'notifications_delivered';
  private static readonly CLICKED_STORAGE_KEY      = 'notifications_clicked';

  private static is_initialized: boolean                                    = false;
  private static has_permission: boolean                                    = false;
  private static session_interval_id: ReturnType<typeof setInterval> | null = null;
  private static session_push_scheduled_until_ms: number                    = 0;
  private static is_foreground_running: boolean                             = false;
  private static launch_events_consumed: boolean                            = false;

  private static isNative(): boolean
  {
    return Capacitor.isNativePlatform();
  }

  private static hasBigPicturePlugin(): boolean
  {
    return Capacitor.isPluginAvailable( 'BigPictureNotification' );
  }

  public static async consumeLaunchEvents(): Promise<void>
  {
    if ( !this.isNative() ) return;

    const had_click = await this.consumePendingNativeEvents();
    if ( had_click ) globalThis.__launchedFromPush = true;
    this.launch_events_consumed = true;
  }

  public static async init(): Promise<void>
  {
    if ( !this.isNative() )
    {
      console.log( '[NotificationManager] skipped (web platform)' );
      return;
    }

    if ( this.is_initialized ) return;

    try
    {
      if ( !this.launch_events_consumed ) await this.consumeLaunchEvents();

      const result = await LocalNotifications.requestPermissions();
      this.has_permission = result.display === 'granted';

      if ( !this.has_permission )
      {
        console.log( '[NotificationManager] permission denied' );
        return;
      }

      await LocalNotifications.addListener( 'localNotificationReceived', notification =>
      {
        void this.onLocalNotificationDelivered( notification );
      } );

      await LocalNotifications.addListener( 'localNotificationActionPerformed', action =>
      {
        void this.onLocalNotificationClicked( action.notification );
      } );

      NotificationConfigService.setOnUpdated( config =>
      {
        this.preloadImages( config );
        void this.onForeground();
      } );
      this.preloadImages( NotificationConfigService.get() );

      this.is_initialized = true;
      console.log( '[NotificationManager] initialized' );
    }
    catch ( error )
    {
      console.error( '[NotificationManager] init failed:', error );
    }
  }

  public static async onForeground(): Promise<void>
  {
    if ( !this.has_permission ) return;
    if ( this.is_foreground_running ) return;

    this.is_foreground_running = true;
    try
    {
      await this.consumePendingNativeEvents();
      await this.cancelSmartNotifications();
      await this.scheduleDailyNotifications();
      this.startSessionPushInterval();
    }
    finally
    {
      this.is_foreground_running = false;
    }
  }

  private static preloadImages( config: NotificationConfig ): void
  {
    if ( !this.hasBigPicturePlugin() ) return;

    const urls = collectHttpImageUrls( config );
    if ( urls.length === 0 ) return;

    BigPictureNotification.preloadImages( { urls } ).catch( error =>
    {
      console.warn( '[NotificationManager] preloadImages failed', error );
    } );
  }

  private static async cancelSmartNotifications(): Promise<void>
  {
    try
    {
      const cfg = NotificationConfigService.get().smart_notifications;
      const cancel_days = Math.max( cfg.schedule_days, this.MAX_CANCEL_DAYS );
      const ids = [
        this.SESSION_PUSH_ID,
        ...Array.from( { length: cancel_days * this.SLOTS_PER_DAY }, ( _, i ) => this.DAILY_SLOTS_START_ID + i )
      ];

      await this.cancelIds( ids );
      this.session_push_scheduled_until_ms = 0;
    }
    catch ( error )
    {
      console.error( '[NotificationManager] cancelSmartNotifications failed:', error );
    }
  }

  private static async cancelIds( ids: number[] ): Promise<void>
  {
    if ( ids.length === 0 ) return;

    await LocalNotifications.cancel( { notifications: ids.map( id => ( { id } ) ) } );

    if ( this.hasBigPicturePlugin() )
      await BigPictureNotification.cancelMany( { ids } );

    if ( ids.includes( this.SESSION_PUSH_ID ) )
      this.session_push_scheduled_until_ms = 0;
  }

  private static async scheduleDailyNotifications(): Promise<void>
  {
    const cfg = NotificationConfigService.get().smart_notifications;
    if ( !cfg.enabled ) return;

    const { install_date } = StatisticsStorage.load();
    const now = Date.now();
    let scheduled = 0;

    for ( let day_offset = 0; day_offset < cfg.schedule_days; day_offset++ )
    {
      const day_start = new Date();
      day_start.setDate( day_start.getDate() + day_offset );
      day_start.setHours( 0, 0, 0, 0 );

      const days_since_install = Math.floor( ( day_start.getTime() - install_date ) / 86400000 );
      const slots = this.getSlotsForDay( day_offset, days_since_install, cfg );

      for ( const slot of slots )
      {
        const notif_time = new Date( day_start );
        notif_time.setHours( slot.hour, slot.minute, 0, 0 );

        if ( notif_time.getTime() <= now ) continue;

        const delay_minutes = ( notif_time.getTime() - now ) / 60000;
        const notif_id      = this.DAILY_SLOTS_START_ID + day_offset * this.SLOTS_PER_DAY + slot.slot_index;
        const { title, body, text_pair } = this.pickRandomText( slot.prefix );

        await this.scheduleNotification( {
          title,
          body,
          large_icon:    cfg.large_icon,
          big_picture:   cfg.big_picture,
          delay_minutes,
          id:            notif_id,
          extra: {
            key:          `smart_${slot.prefix.toLowerCase()}_day${day_offset}`,
            scheduled_at: now,
            text_pair
          }
        } );

        scheduled++;
      }
    }

    console.log( `[NotificationManager] scheduled ${scheduled} daily notifications` );
  }

  private static getSlotsForDay(
    day_offset: number,
    days_since_install: number,
    cfg: SmartNotificationsConfig
  ): INotificationSlot[]
  {
    const is_week1 = days_since_install < 7;
    const week_cfg = is_week1 ? cfg.week1 : cfg.week2_active;
    const result: INotificationSlot[] = [];

    for ( const def of SLOT_DEFS )
    {
      const slot_cfg     = week_cfg[ def.key ];
      const force_enable = !is_week1 && def.key === 'morning' && day_offset >= cfg.inactive_days_threshold;

      if ( !force_enable && !slot_cfg.enabled ) continue;

      result.push( {
        slot_index: def.slot_index,
        prefix:     def.prefix,
        hour:       slot_cfg.hour,
        minute:     slot_cfg.minute
      } );
    }

    return result;
  }

  private static pickRandomText( prefix: string ): { title: string; body: string; text_pair: number }
  {
    const text_pair = Math.floor( Math.random() * this.RANDOM_TEXTS_PER_SLOT ) + 1;
    return {
      title: LocalizationManager.t( `${prefix}_PUSH_NOTIFICATION_TITLE_${text_pair}` ),
      body:  LocalizationManager.t( `${prefix}_PUSH_NOTIFICATION_TEXT_${text_pair}` ),
      text_pair
    };
  }

  private static startSessionPushInterval(): void
  {
    if ( this.session_interval_id !== null )
    {
      clearInterval( this.session_interval_id );
      this.session_interval_id = null;
    }

    void this.scheduleSessionPush();

    this.session_interval_id = setInterval( () =>
    {
      void this.scheduleSessionPush();
    }, this.SESSION_INTERVAL_MS );
  }

  private static async scheduleSessionPush(): Promise<void>
  {
    const cfg = NotificationConfigService.get().smart_notifications;
    if ( !cfg.enabled || !cfg.session_push.enabled ) return;
    if ( !this.has_permission ) return;

    const scheduled_margin_ms = 5_000;
    if ( this.session_push_scheduled_until_ms > Date.now() + scheduled_margin_ms ) return;

    const now               = Date.now();
    const delay_minutes     = cfg.session_push.delay_minutes;
    const session_push_time = now + delay_minutes * 60 * 1000;
    const threshold_ms      = cfg.overlap_threshold_minutes * 60 * 1000;

    const { install_date } = StatisticsStorage.load();
    const today_start = new Date();
    today_start.setHours( 0, 0, 0, 0 );
    const days_since_install = Math.floor( ( today_start.getTime() - install_date ) / 86400000 );
    const today_slots        = this.getSlotsForDay( 0, days_since_install, cfg );

    for ( const slot of today_slots )
    {
      const slot_time = new Date( today_start );
      slot_time.setHours( slot.hour, slot.minute, 0, 0 );

      if ( slot_time.getTime() <= now ) continue;

      if ( Math.abs( slot_time.getTime() - session_push_time ) < threshold_ms )
      {
        const slot_id = this.DAILY_SLOTS_START_ID + slot.slot_index;
        await this.cancelById( slot_id );
        console.log( `[NotificationManager] cancelled overlapping slot ${slot.prefix} (ID=${slot_id})` );
      }
    }

    const { title, body, text_pair } = this.pickRandomText( 'AFTER_SESSION' );

    await this.scheduleNotification( {
      title,
      body,
      large_icon:    cfg.session_push.large_icon,
      big_picture:   cfg.session_push.big_picture,
      delay_minutes,
      id:            this.SESSION_PUSH_ID,
      extra: {
        key:          'after_session',
        scheduled_at: now,
        text_pair
      }
    } );
    this.session_push_scheduled_until_ms = session_push_time;
  }

  public static async scheduleByKey( key: string ): Promise<void>
  {
    if ( !this.has_permission ) return;

    const config = NotificationConfigService.get().notifications.find( n => n.key === key );
    if ( !config )
    {
      console.warn( `[NotificationManager] notification not found by key: ${key}` );
      return;
    }
    if ( !config.enabled ) return;

    await this.scheduleNotificationFromConfig( config );
  }

  private static adjustForQuietHours( date: Date ): Date
  {
    const QUIET_END_HOUR   = 10;
    const QUIET_START_HOUR = 23;

    const h        = date.getHours();
    const is_quiet = h >= QUIET_START_HOUR || h < QUIET_END_HOUR;
    if ( !is_quiet ) return date;

    const next = new Date( date );
    next.setHours( QUIET_END_HOUR, 0, 0, 0 );
    if ( next <= date ) next.setDate( next.getDate() + 1 );

    return next;
  }

  public static async scheduleAllEnabled(): Promise<void>
  {
    if ( !this.has_permission ) return;

    const enabled = NotificationConfigService.get().notifications
      .filter( n => n.enabled )
      .sort( ( a, b ) => a.delay_hours - b.delay_hours );

    if ( enabled.length === 0 ) return;

    const now = Date.now();
    let prev_fire_time   = now;
    let prev_delay_hours = 0;

    for ( const config of enabled )
    {
      const delta_ms             = ( config.delay_hours - prev_delay_hours ) * 3600 * 1000;
      const adjusted_time        = this.adjustForQuietHours( new Date( prev_fire_time + delta_ms ) ).getTime();
      const adjusted_delay_hours = ( adjusted_time - now ) / 3600000;

      await this.scheduleNotificationFromConfig( { ...config, delay_hours: adjusted_delay_hours } );

      prev_fire_time   = adjusted_time;
      prev_delay_hours = config.delay_hours;
    }
  }

  private static async scheduleNotificationFromConfig( config: NotificationItemConfig ): Promise<void>
  {
    try
    {
      await this.cancelIds( [ config.id ] );

      let title: string;
      let body: string;
      let text_pair: number | string = 'custom';

      if ( config.title.toLowerCase() === 'random' )
      {
        const picked = this.pickRandomText( 'AFTER_SESSION' );
        title = picked.title;
        body = picked.body;
        text_pair = picked.text_pair;
      }
      else
      {
        title = config.title;
        body = config.body;
      }

      await this.scheduleNotification( {
        title,
        body,
        large_icon:    config.large_icon,
        big_picture:   config.big_picture,
        delay_minutes: config.delay_hours * 60,
        id:            config.id,
        extra: {
          key:          config.key || `notification_${config.id}`,
          text_pair,
          scheduled_at: Date.now()
        }
      } );
    }
    catch ( error )
    {
      console.error( `[NotificationManager] failed to schedule ${config.key || config.id}`, error );
    }
  }

  public static async scheduleNotification( options: IScheduleNotificationOptions ): Promise<void>
  {
    const {
      title,
      body,
      image,
      large_icon,
      big_picture,
      show_large_icon               = !!( image || large_icon ),
      show_big_picture              = !!( image || big_picture ),
      hide_large_icon_when_expanded = false,
      delay_minutes                 = 1,
      id                            = 5,
      extra                         = {}
    } = options;

    if ( !this.has_permission )
    {
      console.warn( `[NotificationManager] no permission - skipping ID=${id}` );
      return;
    }

    const resolved_big_picture = resolveNotificationImageUrl( big_picture );
    const has_any_image        = !!( image || large_icon || resolved_big_picture );
    let style: NotificationStyle = NotificationStyle.None;

    if ( has_any_image )
    {
      if ( show_large_icon && show_big_picture )
        style = hide_large_icon_when_expanded ? NotificationStyle.BothHideLarge : NotificationStyle.Both;
      else if ( show_large_icon )
        style = NotificationStyle.LargeIcon;
      else if ( show_big_picture )
        style = NotificationStyle.BigPicture;
    }

    try
    {
      if ( this.hasBigPicturePlugin() && style !== NotificationStyle.None )
      {
        await BigPictureNotification.schedule( {
          title,
          body,
          imageUrl:      image || '',
          largeIconUrl:  large_icon || '',
          bigPictureUrl: resolved_big_picture || '',
          delaySeconds:  Math.max( 1, Math.round( delay_minutes * 60 ) ),
          id,
          style,
          extra: JSON.stringify( extra )
        } );
      }
      else
      {
        await LocalNotifications.cancel( { notifications: [ { id } ] } );
        await LocalNotifications.schedule( {
          notifications: [ {
            title,
            body,
            id,
            schedule: {
              at: new Date( Date.now() + delay_minutes * 60 * 1000 ),
              allowWhileIdle: true
            },
            sound:        'default',
            actionTypeId: 'NOTIFICATION',
            extra
          } ]
        } );
      }
    }
    catch ( error )
    {
      console.error( `[NotificationManager] failed to schedule ID=${id}:`, error );
    }
  }

  public static async cancelById( id: number ): Promise<void>
  {
    if ( !this.isNative() ) return;

    try
    {
      await this.cancelIds( [ id ] );
    }
    catch ( error )
    {
      console.error( `[NotificationManager] failed to cancel ID=${id}:`, error );
    }
  }

  public static async getPendingNotifications(): Promise<IPendingNotificationsResult>
  {
    const result: IPendingNotificationsResult = { local_notifications: [], big_picture_ids: [] };
    if ( !this.isNative() ) return result;

    try
    {
      const local_result = await LocalNotifications.getPending();
      result.local_notifications = local_result.notifications || [];
    }
    catch ( error )
    {
      console.error( '[NotificationManager] getPending (local) failed:', error );
    }

    try
    {
      if ( this.hasBigPicturePlugin() )
      {
        const big_picture_result = await BigPictureNotification.getPending();
        result.big_picture_ids = ( big_picture_result.ids || [] ).slice().sort( ( a, b ) => a - b );
      }
    }
    catch ( error )
    {
      console.error( '[NotificationManager] getPending (big picture) failed:', error );
    }

    return result;
  }

  public static async checkPermissions(): Promise<boolean>
  {
    if ( !this.isNative() ) return false;

    try
    {
      const result = await LocalNotifications.checkPermissions();
      return result.display === 'granted';
    }
    catch
    {
      return false;
    }
  }

  private static async consumePendingNativeEvents(): Promise<boolean>
  {
    if ( !this.hasBigPicturePlugin() ) return false;

    try
    {
      const { delivered = [], clicked = [] } = await BigPictureNotification.consumePendingEvents();

      for ( const event of delivered )
        this.trackDelivered( event.id, parseExtra( event.extra ), event.timestamp, 'big_picture' );

      for ( const event of clicked )
        this.trackClicked( event.id, parseExtra( event.extra ), event.timestamp, 'big_picture' );

      return clicked.length > 0;
    }
    catch ( error )
    {
      console.error( '[NotificationManager] consumePendingEvents failed:', error );
      return false;
    }
  }

  private static onLocalNotificationDelivered( notification: LocalNotificationSchema ): void
  {
    this.trackDelivered( notification.id, parseExtra( notification.extra ), Date.now(), 'local' );
  }

  private static onLocalNotificationClicked( notification: LocalNotificationSchema ): void
  {
    globalThis.__launchedFromPush = true;
    this.trackClicked( notification.id, parseExtra( notification.extra ), Date.now(), 'local' );
  }

  private static trackDelivered( id: number, extra: INotificationExtra, delivered_at: number, source: string ): void
  {
    analytics.track( 'notification_delivered', {
      notification_id:   id,
      notification_key:  extra.key ?? 'unknown',
      notification_pair: extra.text_pair ?? 'unknown',
      scheduled_at:      extra.scheduled_at ?? delivered_at,
      delivered_at,
      source
    } );
    this.appendStoredRecord( this.DELIVERED_STORAGE_KEY, { id, key: extra.key, at: delivered_at } );
  }

  private static trackClicked( id: number, extra: INotificationExtra, clicked_at: number, source: string ): void
  {
    analytics.track( 'notification_clicked', {
      notification_id:   id,
      notification_key:  extra.key ?? 'unknown',
      notification_pair: extra.text_pair ?? 'unknown',
      scheduled_at:      extra.scheduled_at ?? clicked_at,
      clicked_at,
      source
    } );
    this.appendStoredRecord( this.CLICKED_STORAGE_KEY, { id, key: extra.key, at: clicked_at } );
  }

  private static appendStoredRecord( storage_key: string, record: IStoredNotificationRecord ): void
  {
    try
    {
      const stored: IStoredNotificationRecord[] = JSON.parse( localStorage.getItem( storage_key ) || '[]' );
      stored.push( record );
      if ( stored.length > this.MAX_STORED_NOTIFICATIONS ) stored.shift();
      localStorage.setItem( storage_key, JSON.stringify( stored ) );
    }
    catch ( error )
    {
      console.error( `[NotificationManager] failed to store ${storage_key}:`, error );
    }
  }

  private static getDebugBanner(): NotificationImageConfig
  {
    const cfg = NotificationConfigService.get();
    return cfg.smart_notifications.session_push.big_picture
      || cfg.smart_notifications.big_picture
      || RANDOM_BANNER_MARKER;
  }

  public static async debugTest1LargeIconOnly(): Promise<void>
  {
    await this.scheduleNotification( {
      title:            "Test 1: Logo Only",
      body:             "Game logo as Large Icon (no big picture)",
      large_icon:       NotificationConfigService.get().smart_notifications.large_icon,
      show_big_picture: false,
      delay_minutes:    this.DEBUG_TEST_DELAY_MINUTES,
      id:               991
    } );
  }

  public static async debugTest2BigPictureOnly(): Promise<void>
  {
    await this.scheduleNotification( {
      title:           "Test 2: Banner Only",
      body:            "Big picture banner (no logo)",
      big_picture:     this.getDebugBanner(),
      show_large_icon: false,
      delay_minutes:   this.DEBUG_TEST_DELAY_MINUTES,
      id:              992
    } );
  }

  public static async debugTest3DifferentImages(): Promise<void>
  {
    await this.scheduleNotification( {
      title:         "Test 3: Logo + Banner",
      body:          "Square logo + wide banner",
      large_icon:    NotificationConfigService.get().smart_notifications.large_icon,
      big_picture:   this.getDebugBanner(),
      delay_minutes: this.DEBUG_TEST_DELAY_MINUTES,
      id:            993
    } );
  }

  public static async debugTest4HideLogo(): Promise<void>
  {
    await this.scheduleNotification( {
      title:                         "Test 4: Hide Logo",
      body:                          "Logo visible, but disappears when expanded",
      large_icon:                    NotificationConfigService.get().smart_notifications.large_icon,
      big_picture:                   this.getDebugBanner(),
      hide_large_icon_when_expanded: true,
      delay_minutes:                 this.DEBUG_TEST_DELAY_MINUTES,
      id:                            994
    } );
  }

  public static async debugTest5NoImage(): Promise<void>
  {
    await this.scheduleNotification( {
      title:         "Test 5: No Image",
      body:          "Simple text notification without any pictures",
      delay_minutes: this.DEBUG_TEST_DELAY_MINUTES,
      id:            995
    } );
  }

  public static async debugTestSessionPushNow(): Promise<void>
  {
    if ( !this.has_permission ) return;

    const cfg = NotificationConfigService.get().smart_notifications;
    const { title, body, text_pair } = this.pickRandomText( 'AFTER_SESSION' );

    await this.scheduleNotification( {
      title,
      body,
      large_icon:    cfg.session_push.large_icon,
      big_picture:   cfg.session_push.big_picture,
      delay_minutes: this.DEBUG_TEST_DELAY_MINUTES,
      id:            996,
      extra:         { key: 'debug_after_session', scheduled_at: Date.now(), text_pair }
    } );
  }

  public static async debugCancelAll(): Promise<void>
  {
    if ( !this.isNative() ) return;

    if ( this.session_interval_id !== null )
    {
      clearInterval( this.session_interval_id );
      this.session_interval_id = null;
    }

    const cancel_days = Math.max( NotificationConfigService.get().smart_notifications.schedule_days, this.MAX_CANCEL_DAYS );
    const ids = [
      this.SESSION_PUSH_ID,
      ...NotificationConfigService.get().notifications.map( n => n.id ),
      ...Array.from( { length: cancel_days * this.SLOTS_PER_DAY }, ( _, i ) => this.DAILY_SLOTS_START_ID + i ),
      991, 992, 993, 994, 995, 996
    ];
    await this.cancelIds( ids );
  }

  public static async debugDescribePending(): Promise<string>
  {
    const pending = await this.getPendingNotifications();
    const lines: string[] = [];

    lines.push( `permission: ${this.has_permission ? 'granted' : 'no'}` );
    lines.push( `big picture pending (${pending.big_picture_ids.length}): ${pending.big_picture_ids.join( ', ' ) || '-'}` );
    lines.push( `local pending (${pending.local_notifications.length}):` );
    for ( const n of pending.local_notifications )
    {
      const at = n.schedule?.at ? new Date( n.schedule.at ).toLocaleString() : '?';
      lines.push( `  #${n.id} ${at} ${n.title ?? ''}` );
    }

    if ( this.session_push_scheduled_until_ms > 0 )
      lines.push( `session push at: ${new Date( this.session_push_scheduled_until_ms ).toLocaleTimeString()}` );

    return lines.join( '\n' );
  }
}
